import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { closing } from '@/api/closing';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

export default function MonthClosingPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // UX β: org dropdown через usePermissionsByOrg, окремий orgs.list не потрібен
  // (availableOrgs повертає лише ті org де є accounting.period.close)
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('accounting.period.close');
  const [orgId, setOrgId] = useState('');
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(2);
  const [error, setError] = useState<string | null>(null);

  if (!orgId && availableOrgs[0]) setOrgId(availableOrgs[0].id);

  const previewQ = useQuery({
    queryKey: ['closing-preview', orgId, year, month],
    queryFn: () => closing.preview(orgId, year, month),
    enabled: !!orgId,
  });

  const close = useMutation({
    mutationFn: () => closing.close(orgId, year, month),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['closing-preview'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const reopen = useMutation({
    mutationFn: () => closing.reopen(orgId, year, month),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['closing-preview'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const data = previewQ.data;
  const isClosed = data?.is_closed ?? false;
  const fr = data ? Number(data.financial_result) : 0;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('cl.title')}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {!isClosed && (
            <button
              className="primary"
              onClick={() => { setError(null); close.mutate(); }}
              disabled={close.isPending || !data || (Number(data.total_income) === 0 && Number(data.total_expenses) === 0)}
            >
              ▶ {t('cl.close_period')}
            </button>
          )}
          {isClosed && (
            <button onClick={() => { setError(null); reopen.mutate(); }} disabled={reopen.isPending}>
              ◀ {t('cl.reopen_period')}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('tb.organization')}</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {availableOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
          {!hasPermissionInAnyOrg && (
            <small style={{ color: '#b45309', fontSize: '0.75rem' }}>
              ⚠ У вас немає прав закривати періоди у жодній організації
            </small>
          )}
        </div>
        <div className="field">
          <label>{t('cl.year')}</label>
          <input type="number" min={2000} max={2100} value={year}
                 onChange={(e) => setYear(Number(e.target.value))} />
        </div>
        <div className="field">
          <label>{t('cl.month')}</label>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{m} — {MONTHS[m]}</option>
            ))}
          </select>
        </div>
      </div>

      {data && (
        <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <h3 style={{ marginTop: 0, color: '#15803d' }}>{t('cl.income')}</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: '#f0fdf4', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem', width: 80 }}>{t('cl.code')}</th>
                  <th style={{ padding: '0.5rem' }}>{t('cl.account')}</th>
                  <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>{t('cl.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.income_lines.length === 0 && (
                  <tr><td colSpan={3} style={{ padding: '0.5rem', opacity: 0.6 }}>{t('cl.nothing')}</td></tr>
                )}
                {data.income_lines.map((ln) => (
                  <tr key={ln.account_code} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.5rem' }}><strong>{ln.account_code}</strong></td>
                    <td style={{ padding: '0.5rem' }}>{ln.account_name}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(ln.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
                  <td colSpan={2} style={{ padding: '0.5rem', textAlign: 'right' }}>{t('cl.total_income')}:</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(data.total_income)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div>
            <h3 style={{ marginTop: 0, color: '#b91c1c' }}>{t('cl.expenses')}</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: '#fef2f2', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem', width: 80 }}>{t('cl.code')}</th>
                  <th style={{ padding: '0.5rem' }}>{t('cl.account')}</th>
                  <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>{t('cl.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.expense_lines.length === 0 && (
                  <tr><td colSpan={3} style={{ padding: '0.5rem', opacity: 0.6 }}>{t('cl.nothing')}</td></tr>
                )}
                {data.expense_lines.map((ln) => (
                  <tr key={ln.account_code} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.5rem' }}><strong>{ln.account_code}</strong></td>
                    <td style={{ padding: '0.5rem' }}>{ln.account_name}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(ln.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
                  <td colSpan={2} style={{ padding: '0.5rem', textAlign: 'right' }}>{t('cl.total_expenses')}:</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(data.total_expenses)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {data && (
        <div style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: 8,
                      background: fr >= 0 ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${fr >= 0 ? '#86efac' : '#fca5a5'}`,
                      textAlign: 'center', fontSize: '1.1rem' }}>
          <div style={{ opacity: 0.8 }}>
            {t('cl.financial_result')} ({MONTHS[month]} {year}):
          </div>
          <div style={{ fontSize: '1.6rem', fontWeight: 700,
                        color: fr >= 0 ? '#15803d' : '#b91c1c', marginTop: '0.25rem' }}>
            {fr >= 0 ? '+' : ''}{fmt(data.financial_result)}
            <span style={{ fontSize: '0.9rem', fontWeight: 400, marginLeft: '0.5rem' }}>
              ({fr >= 0 ? t('cl.profit') : t('cl.loss')})
            </span>
          </div>
        </div>
      )}

      {isClosed && (
        <div style={{ marginTop: '1rem', padding: '0.75rem', borderRadius: 6,
                      background: '#fffbeb', border: '1px solid #fde68a',
                      color: '#92400e' }}>
          ✓ {t('cl.period_closed_notice')}
        </div>
      )}

      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
